<template>

    <SeoHead :seo="seo" />
    <main-layout>
        <PageHeader title="Результаты" />
        <div class="controls-row">
            <div class="table-switcher">
                <button
                    type="button"
                    class="switch-btn"
                    :class="{ active: activeTable === 'championship' }"
                    @click="setActiveTable('championship')"
                >
                    <img
                        :src="COMPETITION_ICONS.championship"
                        alt=""
                        class="switch-btn__icon"
                        aria-hidden="true"
                    />
                    <span class="switch-btn__label">Чемпионат РБ</span>
                </button>
                <button
                    type="button"
                    class="switch-btn"
                    :class="{ active: activeTable === 'cup' }"
                    @click="setActiveTable('cup')"
                >
                    <img
                        :src="COMPETITION_ICONS.cup"
                        alt=""
                        class="switch-btn__icon"
                        aria-hidden="true"
                    />
                    <span class="switch-btn__label">Кубок РБ</span>
                </button>
                <button
                    type="button"
                    class="switch-btn"
                    :class="{ active: activeTable === 'supercup' }"
                    @click="setActiveTable('supercup')"
                >
                    <img
                        :src="COMPETITION_ICONS.supercup"
                        alt=""
                        class="switch-btn__icon"
                        aria-hidden="true"
                    />
                    <span class="switch-btn__label">СуперКубок</span>
                </button>
            </div>

            <div class="season-selector">
                <SeasonSelect
                    v-model="selectedSeason"
                    :seasons="availableSeasons"
                    :multiple="false"
                    :include-all="true"
                    @change="handleSeasonChange"
                />
            </div>
        </div>

        <div class="results-container">
            <history-places-matrix
                v-if="isAllSeasons"
                :matrix="activeHistoryMatrix"
            />
            <template v-else>
                <championship-sub-tabs v-if="activeTable === 'championship'" :standings="standings"
                    :teams="championship.teams" :regular-games="championship.regularGames"
                    :playoff-games="championship.playoffGames" :playoff-games-by-stage="championship.playoffGamesByStage"
                    :five-places="championship.fivePlaces" />
                <supercup-tab v-if="activeTable === 'supercup'" :stages="supercup.stages" />
                <cup-sub-tabs v-if="activeTable === 'cup'" :group-games="cup.groupGames" :cup-groups="cup.cupGroups"
                    :playoff-games="cup.playoffGames" :playoff-teams="cup.playoffTeams"
                    :cup-playoff-games="cup.cupPlayoffGames" />
            </template>
        </div>
    </main-layout>
</template>

<script>
import SeoHead from '@/Components/SeoHead.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import ChampionshipSubTabs from '@/components/features/results/ChampionshipSubTabs.vue';
import SupercupTab from '@/components/features/results/SupercupTab.vue';
import CupSubTabs from '@/components/features/results/CupSubTabs.vue';
import HistoryPlacesMatrix from '@/components/features/results/HistoryPlacesMatrix.vue';
import SeasonSelect from '@/components/filters/SeasonSelect.vue';
import { COMPETITION_ICONS } from '@/utils/skillQualityConfig';
import { parseResultsTab } from '@/utils/resultsUrl';

function readTabFromLocation() {
    if (typeof window === 'undefined') {
        return 'championship';
    }

    const params = new URLSearchParams(window.location.search);
    return parseResultsTab(params.get('tab'));
}

export default {
    name: 'Results',
    components: {
        SeoHead,
        PageHeader,
        ChampionshipSubTabs,
        SupercupTab,
        CupSubTabs,
        HistoryPlacesMatrix,
        SeasonSelect
    },
    props: {
        seo: {
            type: Object,
            default: null,
        },
        // Общие данные
        availableSeasons: {
            type: Array,
            required: true,
            default: () => []
        },
        currentSeasonId: {
            type: Number,
            default: null
        },
        currentSeason: {
            type: String,
            default: ''
        },
        isAllSeasons: {
            type: Boolean,
            default: false,
        },
        historyPlaces: {
            type: Object,
            default: null,
        },
        // Данные для турнирной таблицы
        standings: {
            type: Array,
            required: true,
            default: () => []
        },
        // Данные для вкладки "Чемпионат"
        championship: {
            type: Object,
            required: true,
            default: () => ({
                teams: [],
                regularGames: [],
                playoffGames: []
            })
        },
        // Данные для вкладки "СуперКубок"
        supercup: {
            type: Object,
            required: true,
            default: () => ({
                stages: []
            })
        },
        // Данные для вкладки "Кубок"
        cup: {
            type: Object,
            required: true,
            default: () => ({
                groupGames: [],
                cupGroups: [],
                playoffGames: [],
                playoffTeams: [],
                cupPlayoffGames: []
            })
        }
    },
    data() {
        return {
            COMPETITION_ICONS,
            activeTable: readTabFromLocation(),
            selectedSeason: null
        }
    },
    computed: {
        activeHistoryMatrix() {
            if (!this.historyPlaces) {
                return { columns: [], rows: [] };
            }

            return this.historyPlaces[this.activeTable] || { columns: [], rows: [] };
        },
    },
    watch: {
        isAllSeasons: {
            immediate: true,
            handler(isAll) {
                if (isAll) {
                    this.selectedSeason = 'all';
                }
            },
        },
        currentSeasonId: {
            immediate: true,
            handler(newSeasonId) {
                if (this.isAllSeasons) {
                    return;
                }
                if (newSeasonId != null) {
                    this.selectedSeason = String(newSeasonId);
                }
            }
        },
        availableSeasons: {
            immediate: true,
            handler(seasons) {
                if (this.isAllSeasons) {
                    this.selectedSeason = 'all';
                    return;
                }
                if (seasons.length > 0 && !this.selectedSeason) {
                    const id = this.currentSeasonId || seasons[0]?.id;
                    this.selectedSeason = id != null ? String(id) : null;
                }
            }
        }
    },
    methods: {
        setActiveTable(tab) {
            const next = parseResultsTab(tab);
            this.activeTable = next;
            this.syncTabInUrl(next);
        },
        syncTabInUrl(tab) {
            if (typeof window === 'undefined') {
                return;
            }

            const url = new URL(window.location.href);
            if (tab === 'championship') {
                url.searchParams.delete('tab');
            } else {
                url.searchParams.set('tab', tab);
            }

            window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
        },
        handleSeasonChange(seasonId) {
            this.selectedSeason = seasonId;

            if (typeof window === 'undefined') {
                return;
            }

            const url = new URL(window.location.href);
            url.searchParams.set('season_id', String(seasonId));
            if (this.activeTable && this.activeTable !== 'championship') {
                url.searchParams.set('tab', this.activeTable);
            } else {
                url.searchParams.delete('tab');
            }
            window.location.href = url.toString();
        }
    }
}
</script>

<style lang="scss" scoped>
@import '../../scss/tokens';

.controls-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: $space-3;
    margin-bottom: $space-4;

    @include up($bp-md) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

.table-switcher {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.25rem;
    background: $color-bg-secondary;
    padding: 0.25rem;
    border-radius: $radius-md;

    @include up($bp-md) {
        display: inline-grid;
    }
}

.season-selector {
    display: flex;
    align-items: center;
    width: 100%;

    @include up($bp-md) {
        width: auto;
    }
}

.switch-btn {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;
    justify-content: center;
    min-height: $touch-min;
    padding: $space-2;
    background: transparent;
    border: none;
    border-radius: $radius-sm;
    font-weight: 500;
    color: $color-brand;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:hover {
        color: $color-brand-dark;
    }

    &.active {
        background: $color-bg-primary;
        color: $color-brand;
        box-shadow: $shadow-sm;
    }
}

.switch-btn__icon {
    width: 30px;
    height: 30px;
    object-fit: contain;
    display: block;
    flex-shrink: 0;
}

.switch-btn__label {
    font-size: 0.6875rem;
    line-height: 1.2;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.results-container {
    background: $color-bg-primary;
    border-radius: $radius-md;
    padding: $space-2;
    box-shadow: $shadow;

    @include up($bp-md) {
        padding: $space-2 $space-4;
    }
}

:deep(.championship-sub-tabs) {
    .sub-tabs-switcher {
        background: $color-bg-secondary;
        padding: 0.25rem;
        border-radius: $radius-md;
        margin-bottom: $space-4;

        &.stage-tabs-grid {
            display: grid;
            width: 100%;
            gap: 0.25rem;
            grid-template-columns: repeat(2, minmax(0, 1fr));

            @include up($bp-md) {
                grid-template-columns: 1fr 1fr 1.2fr 0.9fr;
                grid-template-rows: auto auto auto;
                grid-template-areas:
                    "regular quarter semi third"
                    "regular quarter semi final"
                    "regular quarter five five";
            }
        }

        .stage-area-regular { grid-area: regular; }
        .stage-area-quarter { grid-area: quarter; }
        .stage-area-semi { grid-area: semi; }
        .stage-area-third { grid-area: third; }
        .stage-area-final { grid-area: final; }
        .stage-area-five { grid-area: five; }

        .sub-switch-btn {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 0.15rem;
            width: 100%;
            height: 100%;
            min-height: 2.75rem;
            padding: 0.5rem 0.75rem;
            background: transparent;
            border: 1px solid $color-bg-primary;
            border-radius: $radius-sm;
            font-size: 0.875rem;
            font-weight: 500;
            color: $color-brand;
            cursor: pointer;
            transition: all 0.15s ease;
            white-space: normal;
            text-align: center;

            &:hover {
                color: $color-brand-dark;
            }

            &.active {
                background: $color-bg-primary;
                color: $color-brand;
                box-shadow: $shadow-sm;
            }

            &--medal {
                padding: 0.35rem 0.5rem;
            }
        }

        .sub-switch-btn__label {
            white-space: nowrap;
        }

        .sub-switch-btn__medal {
            width: 22px;
            height: 22px;
            object-fit: contain;
            display: block;
            flex-shrink: 0;
        }

        .sub-switch-btn__period {
            font-size: 0.65rem;
            font-weight: 500;
            line-height: 1.2;
            color: #64748b;
            white-space: nowrap;
        }
    }

    .sub-tab-content {
        min-height: 300px;

        .view-switcher {
            display: inline-flex;
            gap: 0.25rem;
            background: $color-bg-secondary;
            padding: 0.25rem;
            border-radius: $radius-sm;
            margin-bottom: 1.5rem;

            .view-btn {
                padding: 0.5rem 1rem;
                background: transparent;
                border: none;
                border-radius: 4px;
                font-size: 0.875rem;
                font-weight: 500;
                color: $color-brand;
                cursor: pointer;
                transition: all 0.15s ease;

                &:hover {
                    color: $color-brand-dark;
                }

                &.active {
                    background: $color-bg-primary;
                    color: $color-brand;
                    box-shadow: $shadow-sm;
                }
            }
        }
    }
}
</style>
